import type { LeadEvent } from '@dnx/contracts';
import { Alert, Badge, Button, Dialog, Spinner } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, CalendarPlus, MessageCircle, Plus, StickyNote, UserCheck, Pencil } from 'lucide-react';
import { BookDialog } from '../agenda/book-dialog.js';
import { PortalAccessBox } from '../contacts/portal-access.js';
import { PatientLinkButton } from '../clinical/patient-link.js';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { LOST_LABELS, SOURCE_LABELS, STAGE_LABELS } from '../lib/labels.js';
import { clock } from '../inbox/queries.js';
import { LeadAttributes, StageControl } from './lead-fields.js';
import { leadQuery } from './queries.js';
import { ContactTags } from '../reception/tags.js';

const when = (iso: string) => {
  const d = new Date(iso);
  const date = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', timeZone: 'America/Sao_Paulo' }).format(d);
  return `${date} · ${clock(iso)}`;
};

function EventRow({ e }: { e: LeadEvent }) {
  const who = e.actorType === 'system' ? 'Sistema' : e.actorName ?? 'Equipe';
  const map = {
    created: { Icon: Plus, text: `Lead criado${e.data.source ? ` · origem ${SOURCE_LABELS[e.data.source as string] ?? e.data.source}` : ''}${e.data.campaign ? ` · “${e.data.campaign as string}”` : ''}` },
    stage_changed: { Icon: ArrowRight, text: `${STAGE_LABELS[e.fromStage ?? ''] ?? ''} → ${STAGE_LABELS[e.toStage ?? ''] ?? ''}${e.data.lostReason ? ` (${LOST_LABELS[e.data.lostReason as string]})` : ''}` },
    assigned: { Icon: UserCheck, text: e.data.to ? 'Responsável alterado' : 'Responsável removido' },
    updated: { Icon: Pencil, text: 'Dados do lead atualizados' },
    note: { Icon: StickyNote, text: null },
  } as const;
  const { Icon, text } = map[e.type];
  return (
    <li className="relative flex gap-3 pb-5 last:pb-0">
      <span className="absolute top-7 bottom-0 left-[13px] w-px bg-border" aria-hidden />
      <span className="relative grid size-7 shrink-0 place-items-center rounded-full border border-border bg-surface text-fg-muted"><Icon className="size-3.5" /></span>
      <div className="min-w-0 flex-1 pt-0.5">
        {text && <p className="text-[13px] text-fg">{text}</p>}
        {e.note && <p className="rounded-md bg-warning-soft px-3 py-2 text-[13px] whitespace-pre-wrap text-fg">{e.note}</p>}
        <p className="mt-1 text-[11.5px] text-fg-subtle">{who} · {when(e.createdAt)}</p>
      </div>
    </li>
  );
}

export function LeadDrawer({ id, onClose, canWrite }: { id: string | undefined; onClose: () => void; canWrite: boolean }) {
  const qc = useQueryClient();
  const { data: lead, isLoading } = useQuery({ ...leadQuery(id ?? ''), enabled: !!id });
  const [note, setNote] = useState('');
  const [booking, setBooking] = useState(false);
  const addNote = useMutation({
    mutationFn: () => api(`/crm/leads/${id}/notes`, { method: 'POST', body: { text: note } }),
    onSuccess: () => { setNote(''); void qc.invalidateQueries({ queryKey: ['crm'] }); },
  });

  return (
    <Dialog open={!!id} onClose={onClose} variant="drawer" title={lead?.contact.name ?? 'Lead'}
      description={lead ? `${lead.contact.maskedPhone ?? ''} · ${SOURCE_LABELS[lead.source]}${lead.campaign ? ` · ${lead.campaign}` : ''}` : undefined}>
      {isLoading || !lead ? <div className="grid h-40 place-items-center"><Spinner className="size-5 text-fg-subtle" label="Carregando" /></div> : (
        <div className="space-y-6 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={lead.stage === 'won' ? 'success' : lead.stage === 'lost' ? 'neutral' : 'primary'}>{STAGE_LABELS[lead.stage]}</Badge>
            {lead.conversationId && (
              <Link to="/conversas" search={{ filtro: 'open', c: lead.conversationId }}>
                <Button size="sm" variant="secondary"><MessageCircle className="size-4" />Abrir conversa</Button>
              </Link>
            )}
            {canWrite && lead.stage !== 'won' && lead.stage !== 'lost' && (
              <Button size="sm" variant="secondary" onClick={() => setBooking(true)}><CalendarPlus className="size-4" />Agendar avaliação</Button>
            )}
            <PatientLinkButton contactId={lead.contact.id} />
          </div>
          {booking && <BookDialog open onClose={() => setBooking(false)} contactId={lead.contact.id} contactName={lead.contact.name} />}
          <ContactTags contactId={lead.contact.id} tags={lead.tags} />
          <StageControl lead={lead} canWrite={canWrite} />
          <LeadAttributes lead={lead} canWrite={canWrite} />
          <PortalAccessBox contactId={lead.contact.id} />

          <section>
            <h3 className="text-sm font-semibold text-fg">Histórico</h3>
            {canWrite && (
              <form className="mt-3" onSubmit={(e) => { e.preventDefault(); if (note.trim()) addNote.mutate(); }}>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} aria-label="Nova nota"
                  placeholder="Anotar algo sobre este lead (só a equipe vê)"
                  className="w-full resize-none rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:border-primary" />
                <div className="mt-2 flex justify-end"><Button size="sm" type="submit" disabled={!note.trim()} loading={addNote.isPending}>Adicionar nota</Button></div>
                {addNote.error && <Alert tone="danger">{addNote.error.message}</Alert>}
              </form>
            )}
            <ol className="mt-4">{lead.events.map((e) => <EventRow key={e.id} e={e} />)}</ol>
          </section>
        </div>
      )}
    </Dialog>
  );
}
